import {isString} from 'lodash';
import {useCallback, useContext, useMemo} from 'react';
import {Select, SelectProps} from '../../components/inputs/combobox/select.tsx';
import {ComboboxDataProps} from '../../components/inputs/combobox/types.ts';
import {DropdownOptionItem, Nullish, OmitDistributive} from '../../types';
import {isBooleanString, isStringArray} from '../../utils/type-guards.ts';
import {AdapterProps, FieldAdapter} from '../field-factory';
import {ReadOnlyContext} from '../readonly-context';
import {
  booleanToStringConverter,
  defaultValueConverter,
  selectNumberConverter,
  ValueConverter,
} from '../value-converters';
import { OptionItemDto } from '../../api/generics.ts';

type InputProps = OmitDistributive<SelectProps, 'onChange' | 'value'>;
type SelectInputProps = InputProps & {
  transformer?: ValueConverter<any, any>;
};

export type $SelectAdapterProps = AdapterProps<SelectInputProps>;

export const $SelectAdapter = (props: FieldAdapter<$SelectAdapterProps>) => {
  const {
    inputProps: {data, enumObject, readOnly, transformer, ...selectProps},
    control,
  } = props;

  const dataProps = {data, enumObject} as ComboboxDataProps;

  const {
    field: {onChange: fieldOnChange, value},
    fieldState: {invalid},
  } = control;

  const _transformers = useMemo(() => {
    return transformer ? transformer : getTransformers(data);
  }, [data, transformer]);

  const {readOnly: _readOnly} = useContext(ReadOnlyContext);

  const _value = useMemo<Nullish<string>>(() => {
    return value !== null && value !== undefined
      ? _transformers.format(value)
      : value;
  }, [_transformers, value]);

  const onChange = useCallback(
    (value: Nullish<string>) => {
      fieldOnChange(value ? _transformers.parseInput(value) : value);
    },
    [fieldOnChange, _transformers],
  );

  return (
    <Select
      error={invalid}
      value={_value}
      onChange={onChange}
      readOnly={_readOnly || readOnly}
      {...selectProps}
      {...dataProps}
    />
  );
};

// Can break when using creatable select with no initial options
const getTransformers = (
  data:
    | DropdownOptionItem[]
    | OptionItemDto<unknown>[]
    | string[]
    | undefined,
) => {
  if (!data || isStringArray(data)) return defaultValueConverter;

  if (data.every((opt) => isBooleanString(opt.value)))
    return booleanToStringConverter;

  if (data.every((opt) => typeof opt.value === 'number'))
    return selectNumberConverter;

  if (data.every((opt) => isString(opt.value))) return defaultValueConverter;

  // eslint-disable-next-line no-restricted-syntax
  console.warn('Unexpected data type for Select component');
  return defaultValueConverter;
};